/* ArcDroid 3.0 — product.js
   PDP engine: variant pill selection, gallery + lightbox, sticky ATC,
   product form -> cart:add event, recommendations fetcher, demo video, Klaviyo BIS hook.
   Cart mutations happen ONLY via the cart:add event contract (cart.js owns the requests). */
(function () {
  'use strict';

  /* ── money formatting (mirrors shop.money_format) ─────────────────── */
  function formatMoney(cents) {
    /* Mirror snippets/money-vat.liquid: add VAT only where it is NOT already
       included in the price (vatPct is 0 in tax-inclusive markets). */
    var vp = parseFloat((window.ArcDroid && window.ArcDroid.vatPct) || 0) || 0;
    if (vp > 0) { cents = Math.round(cents + (cents * vp / 100)); }

    var format = (window.ArcDroid && window.ArcDroid.moneyFormat) || '${{amount}}';
    var value = cents / 100;

    function thousands(str, sep) {
      return str.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, key) {
      var parts;
      switch (key) {
        case 'amount_no_decimals':
          return thousands(Math.round(value).toString(), ',');
        case 'amount_with_comma_separator':
          parts = value.toFixed(2).split('.');
          return thousands(parts[0], '.') + ',' + parts[1];
        case 'amount_no_decimals_with_comma_separator':
          return thousands(Math.round(value).toString(), '.');
        default: /* amount */
          parts = value.toFixed(2).split('.');
          return thousands(parts[0], ',') + '.' + parts[1];
      }
    });
  }

  /* ── product form submit -> cart:add (progressive enhancement) ────── */
  var pendingButtons = [];

  function releaseButtons() {
    while (pendingButtons.length) {
      var b = pendingButtons.pop();
      b.removeAttribute('aria-busy');
      b.disabled = false;
    }
  }
  document.addEventListener('cart:updated', releaseButtons);
  document.addEventListener('cart:error', releaseButtons);

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;
    e.preventDefault();

    var fd = new FormData(form);
    var id = parseInt(fd.get('id'), 10);
    if (!id) return;
    var quantity = parseInt(fd.get('quantity'), 10) || 1;
    var properties = null;
    fd.forEach(function (val, key) {
      var m = /^properties\[(.+)\]$/.exec(key);
      if (m) {
        properties = properties || {};
        properties[m[1]] = val;
      }
    });

    var btn = e.submitter || form.querySelector('button[type="submit"]');
    if (btn && !btn.disabled) {
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
      pendingButtons.push(btn);
    }

    document.dispatchEvent(new CustomEvent('cart:add', {
      detail: { id: id, quantity: quantity, properties: properties, sections: undefined }
    }));
  });

  /* ── per-section PDP init ─────────────────────────────────────────── */
  document.querySelectorAll('[data-pdp]').forEach(function (root) {
    initVariantPicker(root);
    initGallery(root);
    initStickyAtc(root);
  });

  /* ── variant picker ───────────────────────────────────────────────── */
  function initVariantPicker(root) {
    var picker = root.querySelector('[data-variant-picker]');
    if (!picker) return;

    var jsonEl = picker.querySelector('[data-variant-json]');
    var variants = [];
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (err) {
      return;
    }

    picker.addEventListener('change', function (e) {
      if (!e.target.matches('.vpicker__radio')) return;
      var values = [];
      picker.querySelectorAll('.vpicker__radio:checked').forEach(function (r) {
        values[parseInt(r.dataset.optionIndex, 10)] = r.value;
      });
      var normT = function (s) { return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim(); };
      var title = normT(values.join(' / '));
      var variant = null;
      for (var i = 0; i < variants.length; i++) {
        if (normT(variants[i].title) === title) { variant = variants[i]; break; }
      }
      if (!variant) return;
      applyVariant(root, picker, variant);
    });
  }

  function variantStatus(v) {
    if (!v.available) return 'out_of_stock';
    if (v.inventory_quantity <= 0 && v.inventory_policy === 'continue') return 'preorder';
    return 'in_stock';
  }

  function applyVariant(root, picker, v) {
    /* hidden id inputs — picker master (form-associated) + per-form copies */
    root.querySelectorAll('input[name="id"][data-variant-id], input[name="id"][data-buy-id]').forEach(function (input) {
      input.value = v.id;
    });

    /* url ?variant= */
    try {
      var u = new URL(window.location.href);
      u.searchParams.set('variant', v.id);
      history.replaceState({}, '', u);
    } catch (err) { /* noop */ }

    /* price blocks */
    var vp = parseFloat((window.ArcDroid && window.ArcDroid.vatPct) || 0) || 0;
    var taxIncl = (window.ArcDroid && window.ArcDroid.taxesIncluded) || vp > 0;
    root.querySelectorAll('[data-price-wrap] .price-block').forEach(function (block) {
      var html = '<span class="price price-block__current">' + formatMoney(v.price) + '</span>';
      if (v.compare_at_price && v.compare_at_price > v.price) {
        html += '<s class="price price--compare">' + formatMoney(v.compare_at_price) + '</s>';
        html += '<span class="price-block__save">Save ' + formatMoney(v.compare_at_price - v.price) + '</span>';
      }
      if (taxIncl) {
        html += '<span class="vat-note mono-note">incl. VAT</span>';
      }
      block.innerHTML = html;
    });

    /* availability — only when the server status is variant-driven ("auto") */
    var mainBuy = root.querySelector('[data-buy-buttons]');
    var isAuto = mainBuy && mainBuy.dataset.auto === 'true';
    var status = isAuto ? variantStatus(v) : (mainBuy ? mainBuy.dataset.status : 'in_stock');

    if (isAuto) {
      var buys = root.querySelectorAll('[data-buy-buttons]');
      for (var i = 0; i < buys.length; i++) {
        var buy = buys[i];
        if (!buy.querySelector('form') && status !== 'out_of_stock') {
          /* server rendered the OOS state (no form) — reload with ?variant for a correct render */
          window.location.assign(window.location.href);
          return;
        }
        applyStatus(buy, status);
      }
    }

    /* sticky bar */
    var sticky = root.querySelector('[data-sticky-atc]');
    if (sticky) {
      var price = sticky.querySelector('[data-sticky-price]');
      if (price) price.textContent = formatMoney(v.price);
      var dot = sticky.querySelector('[data-sticky-dot]');
      if (dot) {
        dot.className = 'stock-dot ' + (status === 'in_stock' ? 'stock-dot--ok' : status === 'preorder' ? 'stock-dot--preorder' : 'stock-dot--oos');
      }
      var btn = sticky.querySelector('[data-sticky-btn]');
      if (btn) {
        btn.classList.toggle('btn--spark', status === 'preorder');
        btn.classList.toggle('btn--primary', status !== 'preorder');
        btn.textContent = status === 'preorder' ? 'Pre-Order' : 'Add to Cart';
        btn.disabled = status === 'out_of_stock';
      }
    }
  }

  function applyStatus(buy, status) {
    buy.dataset.status = status;

    var dot = buy.querySelector('[data-stock-dot]');
    if (dot) {
      dot.className = 'stock-dot ' + (status === 'in_stock' ? 'stock-dot--ok' : status === 'preorder' ? 'stock-dot--preorder' : 'stock-dot--oos');
    }
    var line = buy.querySelector('[data-stock-line]');
    if (line) line.classList.toggle('stock-line--preorder', status === 'preorder');
    var text = buy.querySelector('[data-stock-text]');
    if (text) {
      text.textContent = status === 'in_stock' ? (buy.dataset.textInstock || '')
        : status === 'preorder' ? (buy.dataset.textPreorder || '')
        : (buy.dataset.textOos || '');
    }

    var atc = buy.querySelector('[data-atc]');
    if (atc) {
      if (status === 'out_of_stock') {
        atc.disabled = true;
        atc.classList.remove('btn--spark');
        atc.textContent = 'Out of Stock';
      } else {
        atc.disabled = false;
        atc.classList.toggle('btn--spark', status === 'preorder');
        atc.classList.toggle('btn--primary', status !== 'preorder');
        atc.textContent = status === 'preorder'
          ? (atc.dataset.labelPreorder || 'Pre-Order')
          : (atc.dataset.labelInstock || 'Add to Cart');
      }
    }

    buy.querySelectorAll('[data-preorder-prop]').forEach(function (input) {
      input.disabled = status !== 'preorder';
    });
  }

  /* ── gallery: scroll-snap carousel + dots + thumbs + lightbox ─────── */
  function initGallery(root) {
    var track = root.querySelector('[data-gallery-track]');
    if (!track) return;

    var slides = Array.prototype.slice.call(track.children);
    var counter = root.querySelector('[data-gallery-counter]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-dot]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-thumb]'));
    var current = 0;

    function goTo(i) {
      track.scrollTo({ left: track.clientWidth * i, behavior: 'smooth' });
    }

    function setCurrent(i) {
      if (i === current || i < 0 || i >= slides.length) return;
      current = i;
      if (counter) counter.textContent = (i + 1) + ' / ' + slides.length;
      dots.forEach(function (d, di) { d.setAttribute('aria-current', di === i ? 'true' : 'false'); });
      thumbs.forEach(function (t, ti) { t.setAttribute('aria-current', ti === i ? 'true' : 'false'); });
      slides.forEach(function (s, si) {
        if (si !== i) {
          s.querySelectorAll('video').forEach(function (vid) { vid.pause(); });
        }
      });
    }

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        setCurrent(Math.round(track.scrollLeft / Math.max(track.clientWidth, 1)));
      });
    }, { passive: true });

    dots.forEach(function (d, i) {
      d.addEventListener('click', function () { goTo(i); });
    });
    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { goTo(i); });
    });

    var prevBtn = root.querySelector('[data-gallery-prev]');
    var nextBtn = root.querySelector('[data-gallery-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(current + 1); });
    var syncNav = function (i) {
      if (prevBtn) prevBtn.disabled = i <= 0;
      if (nextBtn) nextBtn.disabled = i >= slides.length - 1;
    };
    syncNav(0);
    var origSetCurrent = setCurrent;
    setCurrent = function (i) { origSetCurrent(i); syncNav(i); };

    initLightbox(root, track);
  }

  /* ── lightbox with pinch-zoom + pan ───────────────────────────────── */
  function initLightbox(root, track) {
    var dialog = root.querySelector('[data-lightbox]');
    if (!dialog) return;
    var img = dialog.querySelector('[data-lightbox-img]');
    var canvas = dialog.querySelector('[data-lightbox-canvas]');
    var closeBtn = dialog.querySelector('[data-lightbox-close]');

    var scale = 1, tx = 0, ty = 0;
    function apply() {
      img.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
    }
    function reset() { scale = 1; tx = 0; ty = 0; apply(); }
    function close() {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }

    track.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-lightbox-open]');
      if (!btn) return;
      var thumb = btn.querySelector('img');
      img.src = btn.dataset.full;
      img.alt = (thumb && thumb.alt) || '';
      reset();
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      closeBtn.focus();
    });

    closeBtn.addEventListener('click', close);
    dialog.addEventListener('click', function (e) {
      if (suppressClick) { suppressClick = false; return; }
      if (e.target === dialog || e.target === canvas) close();
    });
    dialog.addEventListener('close', reset);

    /* pinch zoom + pan via pointer events */
    var pointers = new Map();
    var startDist = 0, startScale = 1, lastX = 0, lastY = 0;
    var suppressClick = false;

    canvas.addEventListener('pointerdown', function (e) {
      pointers.set(e.pointerId, e);
      suppressClick = false;
      if (pointers.size === 2) {
        var p = Array.from(pointers.values());
        startDist = Math.hypot(p[0].clientX - p[1].clientX, p[0].clientY - p[1].clientY);
        startScale = scale;
      } else if (pointers.size === 1) {
        lastX = e.clientX;
        lastY = e.clientY;
      }
      try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
    });

    canvas.addEventListener('pointermove', function (e) {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, e);
      var p = Array.from(pointers.values());
      if (p.length === 2 && startDist > 0) {
        var dist = Math.hypot(p[0].clientX - p[1].clientX, p[0].clientY - p[1].clientY);
        scale = Math.min(4, Math.max(1, startScale * (dist / startDist)));
        if (scale === 1) { tx = 0; ty = 0; }
        suppressClick = true;
        apply();
      } else if (p.length === 1 && scale > 1) {
        tx += e.clientX - lastX;
        ty += e.clientY - lastY;
        lastX = e.clientX;
        lastY = e.clientY;
        suppressClick = true;
        apply();
      }
    });

    function lift(e) {
      pointers.delete(e.pointerId);
      startDist = 0;
      if (pointers.size === 1) {
        var rest = Array.from(pointers.values())[0];
        lastX = rest.clientX;
        lastY = rest.clientY;
      }
    }
    canvas.addEventListener('pointerup', lift);
    canvas.addEventListener('pointercancel', lift);

    img.addEventListener('dblclick', function () {
      if (scale > 1) {
        reset();
      } else {
        scale = 2.5;
        apply();
      }
    });
  }

  /* ── sticky add-to-cart bar ───────────────────────────────────────── */
  function initStickyAtc(root) {
    var bar = root.querySelector('[data-sticky-atc]');
    if (!bar || !('IntersectionObserver' in window)) return;
    var anchor = root.querySelector('.buybox [data-atc]') || root.querySelector('.buybox');
    if (!anchor) return;

    bar.hidden = false;
    var pastAnchor = false, finalVisible = false, drawerOpen = false;

    function update() {
      if (pastAnchor && !finalVisible && !drawerOpen) bar.setAttribute('data-visible', '');
      else bar.removeAttribute('data-visible');
    }

    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        pastAnchor = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      });
      update();
    }).observe(anchor);

    var finalCta = root.querySelector('[data-final-cta]');
    if (finalCta) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { finalVisible = entry.isIntersecting; });
        update();
      }, { rootMargin: '0px 0px -40px 0px' }).observe(finalCta);
    }

    document.addEventListener('drawer:open', function () { drawerOpen = true; update(); });
    document.addEventListener('drawer:close', function () { drawerOpen = false; update(); });
  }

  /* ── recommendations fetcher (Section Rendering API) ──────────────── */
  document.querySelectorAll('[data-recommendations][data-pending]').forEach(function (el) {
    var url = el.dataset.url;
    if (!url) return;
    fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.querySelector('.recs__cell')) {
          el.innerHTML = fresh.innerHTML;
          el.removeAttribute('data-pending');
        } else {
          el.hidden = true;
        }
      })
      .catch(function () {
        el.hidden = true;
      });
  });

  /* ── demo video facade + Klaviyo BIS hook (delegated) ─────────────── */
  document.addEventListener('click', function (e) {
    var play = e.target.closest('[data-demo-play]');
    if (play) {
      var wrap = play.closest('[data-demo-video]');
      if (!wrap) return;
      var type = wrap.dataset.videoType;
      var id = wrap.dataset.videoId;
      if (!id) return;
      var iframe = document.createElement('iframe');
      iframe.src = type === 'youtube'
        ? 'https://www.youtube.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0'
        : 'https://player.vimeo.com/video/' + encodeURIComponent(id) + '?autoplay=1';
      iframe.title = 'Product video';
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
      iframe.setAttribute('allowfullscreen', '');
      wrap.innerHTML = '';
      wrap.appendChild(iframe);
      return;
    }

    var bis = e.target.closest('[data-klaviyo-bis]');
    if (bis) {
      var hasKlaviyo = !!(window.klaviyo || window._klOnsite || window._learnq);
      if (!hasKlaviyo) {
        var note = bis.parentElement && bis.parentElement.querySelector('[data-klaviyo-note]');
        if (note) note.hidden = false;
      }
    }
  });
})();
